import React, { useEffect } from 'react';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCart, type CartItem } from '@context/CartContext';
import { useAudio } from '@context/AudioContext';
import ImageWithFallback from '@components/common/ImageWithFallback';

// Animations
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const ripple = keyframes`
  0% { transform: scale(0.95); opacity: 1; }
  100% { transform: scale(1.3); opacity: 0; }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(78, 205, 196, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(78, 205, 196, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(78, 205, 196, 0);
  }
`;

// Global animations
const CartAnimations = createGlobalStyle`
  .pulsing {
    animation: ${pulse} 2s infinite;
  }
`;

// Styled Components
const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(3px);
  z-index: ${({ theme }) => theme.zIndex.overlay};
`;

const SidebarContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100%;
  background: ${({ theme }) => theme.colors.surfaceAlt};
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.3);
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 320px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing['5']};
  border-bottom: 1px solid ${({ theme }) => theme.colors.overlay.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SidebarTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-family: ${({ theme }) => theme.fonts.subheading};
  
  background: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.primary} 20%,
    ${({ theme }) => theme.colors.accent1} 40%,
    ${({ theme }) => theme.colors.accent1} 60%,
    ${({ theme }) => theme.colors.primary} 80%
  );
  background-size: 200% auto;
  color: transparent;
  background-clip: text;
  -webkit-background-clip: text;
  animation: ${shimmer} 4s linear infinite;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.overlay.light};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.quick};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: rotate(90deg);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing['5']};
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['8']} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  
  h3 {
    margin-bottom: ${({ theme }) => theme.spacing['4']};
    font-family: ${({ theme }) => theme.fonts.heading};
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing['6']};
  }
  
  svg {
    width: 80px;
    height: 80px;
    fill: ${({ theme }) => theme.colors.textMuted};
    margin-bottom: ${({ theme }) => theme.spacing['4']};
  }
`;

const CartList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CartItem = styled(motion.li)`
  display: flex;
  gap: ${({ theme }) => theme.spacing['4']};
  padding: ${({ theme }) => theme.spacing['3']} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.overlay.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const RippleEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary}40;
  animation: ${ripple} 1.5s ease-out infinite;
`;

const AlbumCover = styled.div<{ isPlaying?: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: ${({ isPlaying }) => isPlaying ? '50%' : '10px'};
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  
  ${({ isPlaying }) => isPlaying && css`
    animation: ${rotate} 20s linear infinite;
  `}
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 15px;
    height: 15px;
    background-color: ${({ theme }) => theme.colors.surfaceAlt};
    border-radius: 50%;
    opacity: ${({ isPlaying }) => isPlaying ? 1 : 0};
    z-index: 2;
    transition: opacity 0.3s ease;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at center,
      transparent 30%,
      rgba(0, 0, 0, 0.5) 100%
    );
    opacity: ${({ isPlaying }) => isPlaying ? 1 : 0};
    z-index: 1;
    transition: opacity 0.3s ease;
  }
`;

const DefaultCover = styled.div<{ isPlaying?: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: ${({ isPlaying }) => isPlaying ? '50%' : '10px'};
  background: ${({ theme }) => theme.colors.overlay.light};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
  
  ${({ isPlaying }) => isPlaying && css`
    animation: ${rotate} 20s linear infinite;
  `}
  
  svg {
    width: 30px;
    height: 30px;
    fill: ${({ theme }) => theme.colors.textMuted};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 15px;
    height: 15px;
    background: ${({ theme }) => theme.colors.surfaceAlt};
    border-radius: 50%;
    opacity: ${({ isPlaying }) => isPlaying ? 1 : 0};
    z-index: 2;
    transition: opacity 0.3s ease;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at center,
      transparent 30%,
      rgba(0, 0, 0, 0.5) 100%
    );
    opacity: ${({ isPlaying }) => isPlaying ? 1 : 0};
    z-index: 1;
    transition: opacity 0.3s ease;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing['1']};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemArtist = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const ItemControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing['2']};
  align-items: center;
`;

const ItemButton = styled.button<{ active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.overlay.light};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.quick};
  
  &:hover {
    transform: scale(1.1);
    background: ${({ theme, active }) => active ? theme.colors.accent1 : theme.colors.primary};
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
  
  /* Animation applied conditionally via className */
`;

const SidebarFooter = styled.div`
  padding: ${({ theme }) => theme.spacing['5']};
  border-top: 1px solid ${({ theme }) => theme.colors.overlay.light};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing['4']};
`;

const CartTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  
  .total-label {
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  .total-value {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
  }
`;

const CheckoutButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing['3']};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent1};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClearButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing['2']};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  border: none;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.quick};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// Animation variants
const slideIn = {
  hidden: { x: 400 },
  visible: {
    x: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 250
    }
  },
  exit: {
    x: 400,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 250
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 250
    }
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.2
    }
  }
};

// Component Props
interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// Cart Sidebar Component
const CartSidebar: React.FC<CartSidebarProps> = ({ isOpen, onClose }) => {
  const { cartItems, removeFromCart, clearCart } = useCart();
  const { play, currentTrack, isPlaying } = useAudio();

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle checkout
  const navigate = useNavigate();

  const handleCheckout = () => {
    // Navigate to contact form with cart items using React Router
    navigate('/contact');
    onClose();
  };

  // Play a track
  const playTrack = (track: CartItem) => {
    play(track);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <CartAnimations />
          <Overlay
            variants={fadeIn}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />

          <SidebarContainer
            variants={slideIn}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <SidebarHeader>
              <SidebarTitle>Your Selections</SidebarTitle>
              <CloseButton onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </CloseButton>
            </SidebarHeader>

            <SidebarContent>
              {cartItems.length > 0 ? (
                <CartList>
                  <AnimatePresence>
                    {cartItems.map((item) => (
                      <CartItem
                        key={item.id}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        layout
                      >
                        {item.coverArt ? (
                          <AlbumCover isPlaying={isPlaying && currentTrack?.id === item.id}>
                            {isPlaying && currentTrack?.id === item.id && <RippleEffect />}
                            <ImageWithFallback
                              src={item.coverArt}
                              alt={item.title}
                            />
                          </AlbumCover>
                        ) : (
                          <DefaultCover isPlaying={isPlaying && currentTrack?.id === item.id}>
                            {isPlaying && currentTrack?.id === item.id && <RippleEffect />}
                            <svg viewBox="0 0 24 24">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 14.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 7.5 12 7.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zm0-5.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z" />
                            </svg>
                          </DefaultCover>
                        )}

                        <ItemInfo>
                          <ItemTitle>{item.title}</ItemTitle>
                          <ItemArtist>{item.artist}</ItemArtist>
                        </ItemInfo>

                        <ItemControls>
                          <ItemButton
                            onClick={() => playTrack(item)}
                            active={isPlaying && currentTrack?.id === item.id}
                            aria-label={isPlaying && currentTrack?.id === item.id ? "Pause" : "Play"}
                            className={isPlaying && currentTrack?.id === item.id ? "pulsing" : ""}
                          >
                            {isPlaying && currentTrack?.id === item.id ? (
                              <svg viewBox="0 0 24 24">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            )}
                          </ItemButton>

                          <ItemButton
                            onClick={() => removeFromCart(item.id)}
                            aria-label="Remove from cart"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M19 13H5v-2h14v2z" />
                            </svg>
                          </ItemButton>
                        </ItemControls>
                      </CartItem>
                    ))}
                  </AnimatePresence>
                </CartList>
              ) : (
                <EmptyCart>
                  <svg viewBox="0 0 24 24">
                    <path d="M15.55 13c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.37-.66-.11-1.48-.87-1.48H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2h7.45zM6.16 6h12.15l-2.76 5H8.53L6.16 6zM7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
                  </svg>
                  <h3>Your cart is empty</h3>
                  <p>
                    Add some groovy tracks to your cart to request licensing information.
                  </p>
                </EmptyCart>
              )}
            </SidebarContent>

            {cartItems.length > 0 && (
              <SidebarFooter>
                <CartTotal>
                  <span className="total-label">Total Tracks:</span>
                  <span className="total-value">{cartItems.length}</span>
                </CartTotal>

                <CheckoutButton
                  onClick={handleCheckout}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Proceed to Licensing Request
                </CheckoutButton>

                <ClearButton
                  onClick={clearCart}
                  whileHover={{ scale: 1.05 }}
                >
                  Clear All Items
                </ClearButton>
              </SidebarFooter>
            )}
          </SidebarContainer>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartSidebar;